/**
 * The Table's editor in the Plot card, in two modes behind one switch.
 *
 * Readings: drop in columns and see the data itself, time first, like a
 * spreadsheet. Adding a column waits for Plot; removing or reordering one only
 * redraws.
 *
 * Summary: what the rows are, and what the columns are - statistics, mostly
 * written by the Assistant. Anything that changes what is counted waits for
 * Plot; a column's label and decimals only redraw.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { allColumns, derivedSignals, endDate, selectedShip, startDate } from '../../state/store.ts';
import {
  addTableColumn,
  plotState,
  removeTableColumn,
  reorderTableColumn,
  setTablePageSize,
  setTableMode,
  setTableGroups,
  setTableMeasureDisplay,
  setTableMeasures,
} from '../../state/plot-state.ts';
import { exprKindOf } from '../../state/request.ts';
import { validateExpr } from '../filters/expr-parser.ts';
import { plotColumnPool } from '../ship/ship-columns.ts';
import { isNumericType } from '../../utils/columns.ts';
import type { TableAgg, TableBucket, TableGroup } from '../../api/types.ts';
import {
  TABLE_AGGS,
  TABLE_BUCKETS,
  TABLE_MAX_COLUMNS,
  TABLE_MAX_GROUPS,
  TABLE_PAGE_SIZES,
  estimateReadings,
  roughCount,
  TABLE_MAX_MEASURES,
  aggInfo,
  measureLabel,
  type TableMeasureState,
} from './table-config.ts';
import '../../ui/column-combobox.ts';
import '../../ui/plain-select.ts';
import '../../ui/expr-input.ts';
import './series-picker.ts';

const GROUP_KINDS = [
  { value: 'time', label: 'Time' },
  { value: 'column', label: 'Values of' },
  { value: 'bands', label: 'Bands of' },
];

const DECIMALS = [
  { value: '', label: 'Auto' },
  ...Array.from({ length: 7 }, (_, i) => ({ value: String(i), label: String(i) })),
];

@customElement('table-controls')
export class TableControls extends LightElement {
  private store = new StoreController(this, [plotState, allColumns, derivedSignals, selectedShip, startDate, endDate]);

  /** Which column's details (label, decimals) are open. */
  @state() private openMeasure: number | null = null;
  /** Measures whose condition field was asked for before anything was typed. */
  @state() private conditionShown = new Set<number>();

  private numeric() {
    return plotColumnPool().filter((c) => c.derived || isNumericType(c.type));
  }

  /** Anything that can split rows: numbers, and text or flags with few values. */
  private groupable() {
    return plotColumnPool().filter((c) => c.derived || isNumericType(c.type) || /VARCHAR|STRING|TEXT|BOOL/i.test(c.type));
  }

  /* ── Rows ─────────────────────────────────────────────── */

  private setGroup(i: number, next: TableGroup): void {
    const groups = [...plotState.value.table_groups];
    groups[i] = next;
    setTableGroups(groups);
  }

  private renderGroup(g: TableGroup, i: number): TemplateResult {
    const pool = g.kind === 'bands' ? this.numeric() : this.groupable();
    return html`<div class="table-editor-row">
      <div class="table-editor-line">
        <plain-select
          class="table-editor-kind"
          .value=${g.kind}
          .options=${GROUP_KINDS}
          .select=${(v: string) =>
            this.setGroup(i, v === 'time' ? { kind: 'time', bucket: 'day' } : { kind: v as 'column' | 'bands', column: g.column ?? '' })}
        ></plain-select>
        ${g.kind === 'time'
          ? html`<plain-select
              class="table-editor-grow"
              .value=${g.bucket ?? 'day'}
              .options=${TABLE_BUCKETS}
              .select=${(v: string) => this.setGroup(i, { kind: 'time', bucket: v as TableBucket })}
            ></plain-select>`
          : html`<column-combobox
              class="table-editor-grow"
              .columns=${pool}
              .value=${g.column ?? ''}
              placeholder=${g.kind === 'bands' ? 'Signal…' : 'Column…'}
              .select=${(name: string) => this.setGroup(i, { ...g, column: name })}
            ></column-combobox>`}
        <button class="remove-btn" title="Stop grouping by this" @click=${() =>
          setTableGroups(plotState.value.table_groups.filter((_, j) => j !== i))}>✕</button>
      </div>
      ${g.kind === 'bands' ? this.renderBands(g, i) : nothing}
    </div>`;
  }

  private renderBands(g: TableGroup, i: number): TemplateResult {
    const edges = g.band_edges?.join(', ') ?? '';
    return html`<div class="table-editor-line table-editor-sub">
      <label class="toggle-hint">Cut at</label>
      <input
        type="text"
        class="table-editor-grow"
        placeholder=${`about ${g.bands ?? 5} bands on round numbers`}
        .value=${edges}
        title="Cut points between bands, e.g. 25, 50, 75. The outer bands are open-ended."
        @change=${(e: Event) => {
          const nums = (e.target as HTMLInputElement).value
            .split(/[,\s]+/)
            .map(Number)
            .filter((n) => Number.isFinite(n));
          const sorted = [...new Set(nums)].sort((a, b) => a - b).slice(0, 11);
          this.setGroup(i, { ...g, band_edges: sorted.length ? sorted : null });
        }}
      />
    </div>`;
  }

  /* ── Columns ──────────────────────────────────────────── */

  private setMeasure(i: number, next: TableMeasureState): void {
    const measures = [...plotState.value.table_measures];
    measures[i] = next;
    setTableMeasures(measures);
  }

  private renderMeasure(m: TableMeasureState, i: number): TemplateResult {
    const info = aggInfo(m.agg);
    const open = this.openMeasure === i;
    const timed = info.column === 'none';
    const showCondition = timed || !!m.where || this.conditionShown.has(i);
    return html`<div class="table-editor-row">
      <div class="table-editor-line">
        <plain-select
          class="table-editor-kind"
          .value=${m.agg}
          .options=${TABLE_AGGS.map((a) => ({ value: a.value, label: a.label, keywords: a.hint }))}
          .select=${(v: string) => this.setMeasure(i, { ...m, agg: v as TableAgg })}
        ></plain-select>
        ${info.column === 'none'
          ? html`<span class="table-editor-grow toggle-hint" title=${info.hint}>${m.agg === 'hours' ? 'time covered' : 'share of time'}</span>`
          : html`<column-combobox
              class="table-editor-grow"
              .columns=${m.agg === 'count' ? plotColumnPool() : this.numeric()}
              .value=${m.column ?? ''}
              placeholder=${info.column === 'optional' ? 'All rows' : 'Signal…'}
              .select=${(name: string) => this.setMeasure(i, { ...m, column: name })}
              .clear=${info.column === 'optional' ? () => this.setMeasure(i, { ...m, column: null }) : null}
            ></column-combobox>`}
        <button
          class="table-editor-more ${open ? 'is-open' : ''}"
          title="Label, decimals and condition"
          aria-expanded=${open ? 'true' : 'false'}
          @click=${() => (this.openMeasure = open ? null : i)}
        >⋯</button>
        <button class="remove-btn" title="Remove this column" @click=${() => {
          this.openMeasure = null;
          setTableMeasures(plotState.value.table_measures.filter((_, j) => j !== i));
        }}>✕</button>
      </div>
      ${showCondition
        ? html`<div class="table-editor-sub">
            <expr-input
              placeholder=${timed ? 'Only where… e.g. "DG1_LOAD" > 85' : 'Only where…'}
              .columns=${plotColumnPool()}
              .value=${m.where ?? ''}
              .validate=${(t: string) => (t.trim() ? validateExpr(t, exprKindOf) : null)}
              .change=${(v: string) => this.setMeasure(i, { ...m, where: v })}
            ></expr-input>
          </div>`
        : nothing}
      ${open
        ? html`<div class="table-editor-line table-editor-sub">
            <input
              type="text"
              class="table-editor-grow"
              maxlength="80"
              placeholder=${measureLabel({ ...m, label: null })}
              .value=${m.label ?? ''}
              title="Column heading"
              @input=${(e: Event) => setTableMeasureDisplay(i, { label: (e.target as HTMLInputElement).value })}
            />
            <plain-select
              class="table-editor-dp"
              label="Decimals"
              .value=${m.decimals == null ? '' : String(m.decimals)}
              .options=${DECIMALS}
              .select=${(v: string) => setTableMeasureDisplay(i, { decimals: v === '' ? null : Number(v) })}
            ></plain-select>
            ${showCondition
              ? nothing
              : html`<button class="btn btn-secondary table-editor-cond" @click=${() => {
                  this.conditionShown = new Set([...this.conditionShown, i]);
                }}>+ Condition</button>`}
          </div>`
        : nothing}
    </div>`;
  }

  private renderMode(): TemplateResult {
    const readings = plotState.value.table_mode === 'readings';
    return html`<div class="axis-block">
      <label>Show</label>
      <div class="seg">
        <button type="button" class="seg-btn ${readings ? 'active' : ''}" aria-pressed=${readings ? 'true' : 'false'}
          title="The data itself: time, then the columns you pick"
          @click=${() => setTableMode('readings')}>Readings</button>
        <button type="button" class="seg-btn ${readings ? '' : 'active'}" aria-pressed=${readings ? 'false' : 'true'}
          title="Statistics: averages, hours at a state, by day or by category"
          @click=${() => setTableMode('summary')}>Summary</button>
      </div>
    </div>`;
  }

  private renderReadings(): TemplateResult {
    const cols = plotState.value.table_columns;
    const full = cols.length >= TABLE_MAX_COLUMNS;
    return html`
      <div class="axis-block">
        <label>Columns <span class="toggle-hint">${cols.length ? `time + ${cols.length}` : 'one or more'}</span></label>
        <series-picker
          .items=${cols}
          .pool=${plotColumnPool()}
          addPlaceholder=${full ? `Limit is ${TABLE_MAX_COLUMNS} columns` : '+ Add column…'}
          .addItem=${(name: string) => addTableColumn(name)}
          .removeItem=${(name: string) => removeTableColumn(name)}
          .reorder=${(from: number, to: number) => reorderTableColumn(from, to)}
        ></series-picker>
      </div>
      <div class="axis-block">
        <label>Rows per page</label>
        <plain-select
          .value=${String(plotState.value.table_page_size)}
          .options=${TABLE_PAGE_SIZES.map((v) => ({ value: String(v), label: v.toLocaleString('en-US') }))}
          .select=${(v: string) => setTablePageSize(Number(v))}
        ></plain-select>
        ${this.renderEstimate()}
      </div>
      <div class="toggle-hint">Every reading in range, oldest first. Filters apply. Drag to reorder.</div>
    `;
  }

  /**
   * About how many readings, and so pages, before anything is fetched - from
   * the ship's metadata, so it costs nothing. Filters only take readings away,
   * so it is an upper bound; the pager has the exact count after Plot.
   */
  private renderEstimate(): TemplateResult | typeof nothing {
    const n = estimateReadings(allColumns.value, startDate.value, endDate.value);
    if (n == null) return nothing;
    const size = plotState.value.table_page_size;
    const pages = Math.max(1, Math.ceil(n / size));
    return html`<div class="toggle-hint" title="From the ship's metadata, before filters. Plot gives the exact count.">
      ≈ ${roughCount(n)} readings in range · ≈ ${pages.toLocaleString('en-US')} ${pages === 1 ? 'page' : 'pages'}
    </div>`;
  }

  override render(): TemplateResult {
    const s = plotState.value;
    if (s.table_mode === 'readings') return html`${this.renderMode()}${this.renderReadings()}`;
    const groups = s.table_groups;
    const measures = s.table_measures;
    return html`
      ${this.renderMode()}
      <div class="axis-block">
        <label>Rows <span class="toggle-hint">${groups.length ? 'one per group' : 'one summary row'}</span></label>
        <div class="table-editor">
          ${groups.map((g, i) => this.renderGroup(g, i))}
          ${groups.length < TABLE_MAX_GROUPS
            ? html`<button class="btn btn-secondary table-editor-add" @click=${() =>
                setTableGroups([...groups, groups.some((g) => g.kind === 'time') ? { kind: 'column', column: '' } : { kind: 'time', bucket: 'day' }])}>
                + Group rows by
              </button>`
            : nothing}
        </div>
      </div>
      <div class="axis-block">
        <label>Columns <span class="toggle-hint">${measures.length ? `${measures.length} of ${TABLE_MAX_MEASURES}` : 'one or more'}</span></label>
        <div class="table-editor">
          ${measures.map((m, i) => this.renderMeasure(m, i))}
          ${measures.length < TABLE_MAX_MEASURES
            ? html`<button class="btn btn-secondary table-editor-add" @click=${() =>
                setTableMeasures([...measures, { agg: 'avg', column: '' }])}>+ Column</button>`
            : nothing}
        </div>
      </div>
      <div class="toggle-hint">
        Every figure is exact, over every reading in range. Hours count each reading's gap to the next, up to 10 minutes.
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'table-controls': TableControls;
  }
}
